import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, MapPin, CalendarDays, LogOut, Compass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import PayDeposit from "@/components/PayDeposit";
import { scrollToSection } from "@/lib/scroll";

export default function AccountPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/my-enquiries");
    setItems(data);
  }, []);

  useEffect(() => {
    if (user === false) navigate("/auth");
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role !== "admin") load().catch(() => setItems([]));
    if (user && user.role === "admin") navigate("/admin");
  }, [user, load, navigate]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="account-loading">
        <Loader2 size={28} className="animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 pt-36 pb-24" data-testid="account-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-[#d4af37] font-medium mb-4">My Account</p>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-slate-50">
              My <span className="italic text-[#d4af37]">trips.</span>
            </h1>
            <p className="text-slate-400 mt-3">Welcome, {user.name} — {user.email}</p>
          </div>
          <button
            data-testid="account-logout-button"
            onClick={async () => { await logout(); navigate("/"); }}
            className="inline-flex items-center gap-2 border border-[#d4af37]/40 text-slate-300 px-5 py-3 text-xs tracking-[0.15em] uppercase hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>

        {items === null ? (
          <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-[#d4af37]" /></div>
        ) : items.length === 0 ? (
          <div className="border border-[#d4af37]/25 bg-[#101C3E]/50 p-12 text-center" data-testid="account-empty">
            <Compass size={32} className="text-[#d4af37] mx-auto mb-5" />
            <p className="font-display text-2xl text-slate-50 mb-3">No journeys yet.</p>
            <p className="text-slate-400 text-sm mb-8">Send us a booking request and it will appear here.</p>
            <Link
              to="/#booking"
              data-testid="account-plan-journey-link"
              className="inline-block bg-[#d4af37] text-[#0A1128] px-8 py-3.5 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#f3e5ab] transition-colors"
            >
              Book Now
            </Link>
          </div>
        ) : (
          <div className="space-y-5" data-testid="account-enquiries-list">
            {items.map((e) => (
              <div
                key={e.id}
                data-testid={`account-enquiry-${e.id}`}
                className="border border-[#d4af37]/20 bg-[#101C3E]/50 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6"
              >
                <div>
                  <h3 className="font-display text-2xl text-slate-50 flex items-center gap-3">
                    <MapPin size={18} className="text-[#d4af37]" />
                    {e.destination || "Destination to be decided"}
                  </h3>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm text-slate-400">
                    {e.travel_date && (
                      <span className="flex items-center gap-2"><CalendarDays size={13} className="text-[#d4af37]" /> {e.travel_date}</span>
                    )}
                    <span>Sent {new Date(e.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                  {e.message && <p className="text-sm text-slate-400 mt-3 max-w-lg">{e.message}</p>}
                </div>
                <div className="shrink-0 flex flex-col items-start md:items-end gap-3">
                  {e.deposit_status === "paid" ? (
                    <span data-testid={`deposit-paid-${e.id}`} className="border border-[#d4af37] text-[#d4af37] px-4 py-2 text-xs tracking-[0.15em] uppercase">
                      Advance Paid
                    </span>
                  ) : (
                    <PayDeposit enquiry={e} onPaid={load} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
